import React from 'react';

export const isClient = typeof window !== 'undefined';
export const isServer = typeof window === 'undefined';

export const getWindow = () => (isClient ? window : null);
export const getDocument = () => (isClient ? document : null);
export const getNavigator = () => (isClient ? navigator : null);
export const getLocalStorage = () => (isClient ? localStorage : null);
export const getSessionStorage = () => (isClient ? sessionStorage : null);

export const safeStringify = (obj) => {
  const seen = new Set();
  return JSON.stringify(obj, (key, value) => {
    if (typeof value === 'object' && value !== null) {
      if (seen.has(value)) {
        return '[Circular Reference]';
      }
      seen.add(value);
    }
    if (typeof value === 'function') {
      return '[Function]';
    }
    return value;
  });
};

export const safeLocalStorage = {
  getItem: (key) => {
    try {
      return isClient ? localStorage.getItem(key) : null;
    } catch (error) {
      console.warn('Error accessing localStorage:', error);
      return null;
    }
  },
  setItem: (key, value) => {
    try {
      if (isClient) {
        localStorage.setItem(key, value);
        return true;
      }
      return false;
    } catch (error) {
      console.warn('Error setting localStorage:', error);
      return false;
    }
  },
  removeItem: (key) => {
    try {
      if (isClient) {
        localStorage.removeItem(key);
        return true;
      }
      return false;
    } catch (error) {
      console.warn('Error removing from localStorage:', error);
      return false;
    }
  },
  clear: () => {
    try {
      if (isClient) {
        localStorage.clear();
        return true;
      }
      return false;
    } catch (error) {
      console.warn('Error clearing localStorage:', error);
      return false;
    }
  },
};

export const safeSessionStorage = {
  getItem: (key) => {
    try {
      return isClient ? sessionStorage.getItem(key) : null;
    } catch (error) {
      console.warn('Error accessing sessionStorage:', error);
      return null;
    }
  },
  setItem: (key, value) => {
    try {
      if (isClient) {
        sessionStorage.setItem(key, value);
        return true;
      }
      return false;
    } catch (error) {
      console.warn('Error setting sessionStorage:', error);
      return false;
    }
  },
  removeItem: (key) => {
    try {
      if (isClient) {
        sessionStorage.removeItem(key);
        return true;
      }
      return false;
    } catch (error) {
      console.warn('Error removing from sessionStorage:', error);
      return false;
    }
  },
  clear: () => {
    try {
      if (isClient) {
        sessionStorage.clear();
        return true;
      }
      return false;
    } catch (error) {
      console.warn('Error clearing sessionStorage:', error);
      return false;
    }
  },
};

export const useLocalStorage = (key, initialValue) => {
  const [storedValue, setStoredValue] = React.useState(() => {
    try {
      const item = safeLocalStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.warn('Error reading from localStorage:', error);
      return initialValue;
    }
  });

  const setValue = (value) => {
    try {
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      safeLocalStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      console.warn('Error setting localStorage:', error);
    }
  };

  return [storedValue, setValue];
};

export const useSessionStorage = (key, initialValue) => {
  const [storedValue, setStoredValue] = React.useState(() => {
    try {
      const item = safeSessionStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.warn('Error reading from sessionStorage:', error);
      return initialValue;
    }
  });

  const setValue = (value) => {
    try {
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      safeSessionStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      console.warn('Error setting sessionStorage:', error);
    }
  };

  return [storedValue, setValue];
};
